/**
 * Terac's published listings in AI app directories.
 *
 * One entry per directory, because the surfaces that need this data keep
 * multiplying: the /mcp install section, a landing page per listing, the
 * in-product integrations page, the site llms.txt, and the funnel events that
 * split by client. The ChatGPT launch grew all of those as bespoke code, and
 * adding Claude by copying it would have doubled every one. A third directory
 * should be an entry here and nothing else.
 *
 * `clientName` is the awkward one, and it is deliberately an EXACT match.
 *
 * Connectors self-register through RFC 7591 and nothing overrides better-auth's
 * id generator, so every client_id is 32 random letters; no id carries a vendor
 * anywhere in it, and matching on one recognizes nothing. The registered name is
 * all there is, and the registration endpoint is unauthenticated by necessity,
 * so a substring test would let anyone light a card up by registering something
 * called "my claude helper". The OAuthClient table already contains
 * "Terac Sync (looks legit)", "pwn2" and "odin-fresh-evil" from a past
 * pen-test, so that is not hypothetical.
 *
 * Exact whole-name matching is also what keeps the two Anthropic clients apart:
 * the directory app registers as "Claude", while the CLI registers as
 * "Claude Code (terac)" and must NOT light up the Claude card.
 *
 * Nothing security-bearing keys off this: it decides which card says
 * "Connected", never what anyone is allowed to do.
 */
export interface McpAppDirectory {
  /** Stable id. Used in analytics and as the landing-page slug. */
  id: "chatgpt" | "claude";
  /** How the client is named in copy. */
  name: string;
  /** The public listing. One click, no developer mode, no URL to paste. */
  listingUrl: string;
  /** Our landing page for people arriving from that listing. */
  landingPath: string;
  /** One line, for the integrations card and the nav description. */
  tagline: string;
  /**
   * The exact name this client registers, lowercased. Compared whole; see the
   * module comment for why a substring test is unsafe here.
   */
  clientName: string;
}

export const MCP_APP_DIRECTORIES: readonly McpAppDirectory[] = [
  {
    id: "chatgpt",
    name: "ChatGPT",
    listingUrl:
      "https://chatgpt.com/plugins/plugin_asdk_app_699562017464819186082ec0bff33f17",
    landingPath: "/chatgpt",
    tagline: "Price, launch, and review research from a ChatGPT conversation",
    clientName: "chatgpt",
  },
  {
    id: "claude",
    name: "Claude",
    listingUrl: "https://claude.ai/directory/terac",
    landingPath: "/claude",
    tagline: "Price, launch, and review research from a Claude conversation",
    clientName: "claude",
  },
] as const;

export function getAppDirectory(id: McpAppDirectory["id"]): McpAppDirectory {
  const found = MCP_APP_DIRECTORIES.find((entry) => entry.id === id);
  if (!found) {
    throw new Error(`No MCP app directory registered for "${id}"`);
  }
  return found;
}

/**
 * Every directory except this one, in registry order.
 *
 * For copy that offers the reader an alternative client. Derived rather than
 * written per page on purpose: the hardcoded version of that line said "Using
 * Claude, Cursor, or something else?" on both landing pages, which pointed
 * /claude's own audience away from the install directly above it. A third
 * directory would have reintroduced the same bug by hand.
 */
export function otherAppDirectories(
  id: McpAppDirectory["id"],
): readonly McpAppDirectory[] {
  return MCP_APP_DIRECTORIES.filter((entry) => entry.id !== id);
}

/**
 * Whether an OAuth binding came from this directory's client.
 *
 * Name only, matched whole. The binding's `clientId` is not consulted: it is 32
 * random letters and identifies nothing.
 */
export function bindingMatchesDirectory(
  directory: McpAppDirectory,
  binding: { clientName?: string | null },
): boolean {
  return binding.clientName?.trim().toLowerCase() === directory.clientName;
}
